import PropTypes from "prop-types";
import React, { useState } from "react";

function PhoneVerificationStep({
    formData,
    updateFormData,
    handlePhoneChange,
    nextStep,
    prevStep,
    handleSendOtp,
    otpLoading,
    otpVerified,
    phoneVerificationEnabled,
    configLoading,
}) {
    const [errors, setErrors] = useState({});

    // Scroll to top on mount
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const validatePhone = () => {
        const newErrors = {};

        if(!formData.phone?.trim()) {
            newErrors.phone = "Phone number is required";
        } else {
            // US phone number validation
            const phoneDigits = formData.phone.replace(/\D/g, "");
            if(phoneDigits.length !== 10) {
                newErrors.phone =
                    "Please enter a valid 10-digit US phone number";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = async () => {
        if(!validatePhone()) return;

        if(!phoneVerificationEnabled || configLoading) {
            // Skip OTP verification if disabled or loading
            nextStep();
            return;
        }

        if(otpVerified) {
            // Already verified, proceed
            nextStep();
            return;
        }

        // Send OTP and wait for result before proceeding
        const success = await handleSendOtp();
        if(success) {
            nextStep();
        }
    };

    const handleInputChange = (field, value) => {
        // Use specific phone change handler if available and field is phone
        if(field === "phone" && handlePhoneChange) {
            handlePhoneChange(value);
        } else {
            updateFormData({ [field]: value });
        }

        // Clear error when user starts typing
        if(errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const formatPhoneNumber = (value) => {
        // Remove all non-numeric characters
        const phoneNumber = value.replace(/\D/g, "");

        // Format as (XXX) XXX-XXXX
        if(phoneNumber.length >= 10) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
                3,
                6,
            )}-${phoneNumber.slice(6, 10)}`;
        } else if(phoneNumber.length >= 6) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
                3,
                6,
            )}-${phoneNumber.slice(6)}`;
        } else if(phoneNumber.length >= 3) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        }
        return phoneNumber;
    };

    return (
        <div className="w-full max-w-lg px-4 pt-8 pb-4">
            <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                    {phoneVerificationEnabled
                        ? "Verify your phone number"
                        : "Enter your phone number"}
                </h1>
                <p className="text-gray-600">
                    {phoneVerificationEnabled
                        ? "Enter your phone number and we will send a one-time code to your phone."
                        : "Please provide your phone number for your account."}
                </p>
            </div>

            <div className="space-y-6">
                {/* Phone Number Input */}
                <div className="flex">
                    {/* Country Code */}
                    <div className="flex items-center bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg px-3 py-4">
                        <img
                            src="https://flagcdn.com/us.svg"
                            alt="US Flag"
                            className="w-5 h-5 mr-2"
                        />
                        <span className="text-gray-700 font-medium">+1</span>
                    </div>

                    {/* Phone Input */}
                    <div className="flex-1">
                        <input
                            type="tel"
                            placeholder="(555) 123-4567"
                            value={formData.phone}
                            onChange={(e) => {
                                const formatted = formatPhoneNumber(
                                    e.target.value,
                                );
                                handleInputChange("phone", formatted);
                            }}
                            maxLength={14}
                            className={`w-full p-4 border rounded-r-lg text-lg ${
                                errors.phone
                                    ? "border-red-500 focus:ring-red-500"
                                    : "border-gray-300 focus:ring-navy focus:border-navy"
                            }`}
                        />
                    </div>
                </div>

                {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}

                {configLoading && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-600 text-sm">
                            Loading phone verification settings...
                        </p>
                    </div>
                )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-8">
                <button
                    onClick={prevStep}
                    className="px-8 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-all"
                >
                    Back
                </button>

                <button
                    onClick={handleNext}
                    disabled={otpLoading}
                    className={`px-8 py-3 rounded-lg font-semibold transition-all shadow-lg ${
                        otpLoading
                            ? "bg-gray-400 text-white cursor-not-allowed"
                            : "bg-navy text-white hover:bg-navy-2"
                    }`}
                >
                    {otpLoading ? "Sending..." : "Next"}
                </button>
            </div>
        </div>
    );
}

PhoneVerificationStep.propTypes = {
    formData: PropTypes.object.isRequired,
    updateFormData: PropTypes.func.isRequired,
    handlePhoneChange: PropTypes.func,
    nextStep: PropTypes.func.isRequired,
    prevStep: PropTypes.func.isRequired,
    handleSendOtp: PropTypes.func.isRequired,
    otpLoading: PropTypes.bool.isRequired,
    otpVerified: PropTypes.bool.isRequired,
    phoneVerificationEnabled: PropTypes.bool.isRequired,
    configLoading: PropTypes.bool.isRequired,
};

export default PhoneVerificationStep;
